import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    fetchCompanies,
    resetCompanies,
} from '@perpay-web/fintech/actions/ui/companies';
import CompanyPicker from '@perpay-web/fintech/components/composite/CompanyPicker/CompanyPicker';
import CompanyPickerV2 from '@perpay-web/fintech/components/composite/CompanyPickerV2/CompanyPickerV2';
import {
    getError,
    getHasLoadedResults,
    getIsLoadingResults,
    getMatches,
    getSuggestedMatches,
    getTopMatches,
    getPossibleMatches,
} from '@perpay-web/fintech/selectors/ui/companies';

/**
 * Container component for CompanyPicker
 * @param {Object} props - Component props
 * @param {Function} [props.onInputValueChange] - Callback for input value change
 * @returns {JSX.Element} CompanyPicker component
 */
const CompanyPickerContainer = ({
    onInputValueChange: ownOnInputValueChange,
    ...ownProps
}) => {
    const dispatch = useDispatch();

    const error = useSelector(getError);
    const hasLoadedResults = useSelector(getHasLoadedResults);
    const isLoadingResults = useSelector(getIsLoadingResults);
    const matches = useSelector(getMatches);
    const possibleMatches = useSelector(getPossibleMatches);
    const suggestedMatches = useSelector(getSuggestedMatches);
    const topMatches = useSelector(getTopMatches);

    const onInputValueChange = useCallback(
        (value) => {
            dispatch(fetchCompanies(value));
            if (ownOnInputValueChange) {
                ownOnInputValueChange(value);
            }
        },
        [dispatch, ownOnInputValueChange],
    );

    const onUnmount = useCallback(
        () => dispatch(resetCompanies({})),
        [dispatch],
    );

    return (
        <CompanyPicker
            error={error}
            hasLoadedResults={hasLoadedResults}
            isLoadingResults={isLoadingResults}
            matches={matches}
            possibleMatches={possibleMatches}
            suggestedMatches={suggestedMatches}
            topMatches={topMatches}
            onInputValueChange={onInputValueChange}
            onUnmount={onUnmount}
            {...ownProps}
        />
    );
};

export default CompanyPickerContainer;

/**
 * Container component for CompanyPickerV2
 * @param {Object} props - Component props
 * @param {Function} [props.onInputValueChange] - Callback for input value change
 * @returns {JSX.Element} CompanyPickerV2 component
 */
export const CompanyPickerContainerV2 = ({
    onInputValueChange: ownOnInputValueChange,
    ...ownProps
}) => {
    const dispatch = useDispatch();

    const error = useSelector(getError);
    const hasLoadedResults = useSelector(getHasLoadedResults);
    const isLoadingResults = useSelector(getIsLoadingResults);
    const matches = useSelector(getMatches);
    const possibleMatches = useSelector(getPossibleMatches);
    const suggestedMatches = useSelector(getSuggestedMatches);
    const topMatches = useSelector(getTopMatches);

    const onInputValueChange = useCallback(
        (value) => {
            dispatch(fetchCompanies(value));
            if (ownOnInputValueChange) {
                ownOnInputValueChange(value);
            }
        },
        [dispatch, ownOnInputValueChange],
    );

    const onUnmount = useCallback(
        () => dispatch(resetCompanies({})),
        [dispatch],
    );

    return (
        <CompanyPickerV2
            error={error}
            hasLoadedResults={hasLoadedResults}
            isLoadingResults={isLoadingResults}
            matches={matches}
            possibleMatches={possibleMatches}
            suggestedMatches={suggestedMatches}
            topMatches={topMatches}
            onInputValueChange={onInputValueChange}
            onUnmount={onUnmount}
            {...ownProps}
        />
    );
};
